// The Verifactu row's text (subdash.f.verifactuAeat) lives in this bundle, which
// otherwise only loads with the invoice pages: on a direct load of the plan
// page (back from the payment gateway, the locked screen) or of the landing,
// the cards showed the raw key.
import '../locales/verifactu';

/**
 * Plans, prices and feature matrix — the single source of truth shared by the
 * public pricing section (LandingPage) and the account plan cards
 * (SubscriptionDashboard). Both render the same rows and the same figures, so
 * the two surfaces cannot drift apart the way they did before.
 *
 * Prices are quoted IVA INCLUDED. The annual column is the same plan paid for a
 * year at the price of ten months (annualBilled = two months free), which is
 * where the "AHORRAS UN 17%" badge comes from. plans.price in Supabase holds
 * annualBilled — see backend/update_plan_prices_2026.sql.
 *
 * @param {Function} t         i18next translator
 * @param {boolean}  isSpain   ES vs PK wording for the e-invoice row
 */
export function buildPlans(t, isSpain = true) {
    return [
        {
            name: "Plan Básico",
            subtitle: t('subdash.basicSubtitle'),
            priceMonthly: 10,
            priceAnnual: 8.33,
            annualBilled: 100,
            features: [
                { text: t('subdash.f.clients0to5'), included: true },
                { text: t('subdash.f.users1'), included: true },
                { text: t('subdash.f.products10'), included: true },
                { text: t('subdash.f.digitize5'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                ...(isSpain ? [{ text: t('subdash.f.verifactuAeat'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" }] : []),
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.emailSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: false },
                { text: t('subdash.f.docTracking'), included: false },
                { text: t('subdash.f.autoPaymentReminder'), included: false },
                { text: t('subdash.f.bulkSend'), included: false },
                { text: t('subdash.f.recurringExpenses'), included: false },
                { text: t('subdash.f.recurringInvoices'), included: false },
                { text: t('subdash.f.bankReconciliation'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: false },
                { text: t('subdash.f.customSenderEmail'), included: true },
                { text: t('subdash.f.accountingExport'), included: false },
                { text: t('subdash.f.expenseInbox'), included: false },
                { text: t('subdash.f.pdfSplit'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.shopifyIntegration'), included: false, badgeAfter: t('subdash.comingSoon') }
            ]
        },
        {
            name: "Plan Pro",
            subtitle: t('subdash.proSubtitle'),
            priceMonthly: 20,
            priceAnnual: 16.67,
            annualBilled: 200,
            popular: true,
            features: [
                { text: t('subdash.f.clients6to50'), included: true },
                { text: t('subdash.f.users3'), included: true },
                { text: t('subdash.f.products50'), included: true },
                { text: t('subdash.f.digitize50'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                ...(isSpain ? [{ text: t('subdash.f.verifactuAeat'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" }] : []),
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.emailChatSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: true },
                { text: t('subdash.f.docTracking'), included: true },
                { text: t('subdash.f.autoPaymentReminder'), included: true },
                { text: t('subdash.f.bulkSend'), included: true },
                { text: t('subdash.f.recurringExpenses'), included: true },
                { text: t('subdash.f.recurringInvoices'), included: true },
                { text: t('subdash.f.bankReconciliation'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: false },
                { text: t('subdash.f.customSenderEmail'), included: true },
                { text: t('subdash.f.accountingExport'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.expenseInbox'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.pdfSplit'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.shopifyIntegration'), included: false, badgeAfter: t('subdash.comingSoon') }
            ]
        },
        {
            name: "Plan Ilimitado",
            subtitle: t('subdash.unlimitedSubtitle'),
            priceMonthly: 30,
            priceAnnual: 25,
            annualBilled: 300,
            features: [
                { text: t('subdash.f.unlimitedClients'), included: true },
                { text: t('subdash.f.users5'), included: true },
                { text: t('subdash.f.moreUsersContact'), included: true },
                { text: t('subdash.f.unlimitedProducts'), included: true },
                { text: t('subdash.f.digitize250'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                ...(isSpain ? [{ text: t('subdash.f.verifactuAeat'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" }] : []),
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.emailChatPhoneSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: true },
                { text: t('subdash.f.docTracking'), included: true },
                { text: t('subdash.f.autoPaymentReminder'), included: true },
                { text: t('subdash.f.bulkSend'), included: true },
                { text: t('subdash.f.recurringExpenses'), included: true },
                { text: t('subdash.f.recurringInvoices'), included: true },
                { text: t('subdash.f.bankReconciliation'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: true },
                { text: t('subdash.f.customSenderEmail'), included: true },
                { text: t('subdash.f.accountingExport'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.expenseInbox'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.pdfSplit'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.shopifyIntegration'), included: false, badgeAfter: t('subdash.comingSoon') }
            ]
        },
        {
            // Quoted per client: no price is shown and the card links to sales.
            name: "Plan a Medida",
            subtitle: t('subdash.customSubtitle'),
            custom: true,
            features: [
                { text: t('subdash.f.customizationOnDemand'), included: true, badge: t('subdash.badgeCustom'), badgeColor: "bg-[#fca311]" },
                { text: t('subdash.f.customIntegrations'), included: true },
                { text: t('subdash.f.unlimitedClients'), included: true },
                { text: t('subdash.f.unlimitedUsers'), included: true },
                { text: t('subdash.f.unlimitedProducts'), included: true },
                { text: t('subdash.f.digitizeUnlimited'), included: true },
                { text: t('subdash.f.unlimitedDocs'), included: true },
                { text: isSpain ? t('subdash.f.einvoiceEs') : t('subdash.f.einvoicePk'), included: true },
                ...(isSpain ? [{ text: t('subdash.f.verifactuAeat'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" }] : []),
                { text: t('subdash.f.multiDesign'), included: true },
                { text: t('subdash.f.customEmails'), included: true },
                { text: t('subdash.f.connectedManager'), included: true },
                { text: t('subdash.f.dedicatedSupport'), included: true },
                { text: t('subdash.f.mobileApp'), included: true },
                { text: t('subdash.f.stockManagement'), included: true },
                { text: t('subdash.f.analyticsPanel'), included: true },
                { text: t('subdash.f.docTracking'), included: true },
                { text: t('subdash.f.autoPaymentReminder'), included: true },
                { text: t('subdash.f.bulkSend'), included: true },
                { text: t('subdash.f.recurringExpenses'), included: true },
                { text: t('subdash.f.recurringInvoices'), included: true },
                { text: t('subdash.f.bankReconciliation'), included: true, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.bankRemittances'), included: true },
                { text: t('subdash.f.customSenderEmail'), included: true },
                { text: t('subdash.f.accountingExport'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.expenseInbox'), included: true, badge: t('subdash.badgeNew'), badgeColor: "bg-orange-500" },
                { text: t('subdash.f.pdfSplit'), included: false, badgeAfter: t('subdash.comingSoon') },
                { text: t('subdash.f.shopifyIntegration'), included: false, badgeAfter: t('subdash.comingSoon') }
            ]
        }
    ];
}
